import { Locale } from '@front/i18n';

import { Country, Currency, DistanceUnit, Timezone, TLD } from '../types/country';
import { reverseRecord } from '../types/helpers';
import { UniverseReference } from '../types/commons';

export const COUNTRIES: Record<Country, Country> = {
  FR: 'FR',
  GB: 'GB',
  DE: 'DE',
  AT: 'AT',
  CH: 'CH',
};

export const SUPPORTED_COUNTRIES = Object.values(COUNTRIES);

export const DEFAULT_COUNTRY = COUNTRIES.FR;

// No whitelists in web, as we can control on which TLDs we deploy our web apps
export const PRO_APP_SUPPORTED_COUNTRIES: Country[] = ['FR', 'GB', 'DE', 'AT', 'CH'];

export type ProCountryPermission = 'register' | 'onboard' | 'work';

export const PRO_PERMISSIONS_BY_COUNTRY: Record<Country, ProCountryPermission[]> = {
  FR: ['register', 'onboard', 'work'],
  GB: ['register', 'onboard', 'work'],
  DE: ['register', 'onboard', 'work'],
  AT: ['register', 'onboard', 'work'],
  CH: ['register', 'onboard', 'work'],
};

export type AppAvailability = 'available' | 'availableSoon' | 'notAvailable';

export const CUSTOMER_APP_AVAILABILITY_BY_COUNTRY: Record<Country, AppAvailability> = {
  FR: 'available',
  GB: 'available',
  DE: 'availableSoon',
  AT: 'availableSoon',
  CH: 'availableSoon',
};

export const COUNTRIES_BY_DOMAIN: Record<TLD, Country> = {
  '.co.uk': 'GB',
  '.fr': 'FR',
  '.de': 'DE',
  '.at': 'AT',
  '.ch': 'CH',
};

export const DOMAINS_BY_COUNTRY: Record<Country, TLD> = reverseRecord(COUNTRIES_BY_DOMAIN);

export const LOCALES_BY_COUNTRY: Record<Country, Locale[]> = {
  FR: ['fr'],
  GB: ['en'],
  DE: ['de'],
  AT: ['de-AT'],
  CH: ['de-CH', 'fr-CH'],
};

export const COUNTRY_BY_LOCALE: Record<Locale, Country> = {
  fr: 'FR',
  en: 'GB',
  de: 'DE',
  'fr-CH': 'CH',
  'de-AT': 'AT',
  'de-CH': 'CH',
};

export const SUPPORTED_LOCALES = Object.values(LOCALES_BY_COUNTRY).flat();

export const DEFAULT_LOCALE_BY_COUNTRY: Record<Country, Locale> = {
  FR: 'fr',
  GB: 'en',
  DE: 'de',
  AT: 'de-AT',
  CH: 'de-CH',
};

export const PRISMIC_LOCALE_BY_LOCALE: Record<Locale, string> = {
  fr: 'fr-fr',
  en: 'en-gb',
  de: 'de-de',
  'de-AT': 'de-at',
  'de-CH': 'de-ch',
  'fr-CH': 'fr-ch',
};

export const CURRENCIES_BY_COUNTRY: Record<Country, Currency> = {
  FR: 'EUR',
  DE: 'EUR',
  AT: 'EUR',
  CH: 'CHF',
  GB: 'GBP',
};

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  EUR: '€',
  GBP: '£',
  CHF: 'CHF',
};

export const TIMEZONES_BY_COUNTRY: Record<Country, Timezone> = {
  FR: 'Europe/Paris',
  GB: 'Europe/London',
  DE: 'Europe/Berlin',
  AT: 'Europe/Vienna',
  CH: 'Europe/Zurich',
};

export const DISTANCE_UNITS_BY_COUNTRY: Record<Country, DistanceUnit> = {
  FR: 'kilometer',
  GB: 'mile',
  DE: 'kilometer',
  AT: 'kilometer',
  CH: 'kilometer',
};

export const WECASA_HOMEPAGE_LINK_BY_COUNTRY: Record<Country, string> = {
  FR: 'https://www.wecasa.fr',
  GB: 'https://www.wecasa.co.uk',
  DE: 'https://www.wecasa.de',
  AT: 'https://www.wecasa.at',
  CH: 'https://www.wecasa.ch',
};

export const FORGOTTEN_PASSWORD_URL_BY_COUNTRY: Record<Country, string> = {
  FR: 'espace-client/mot-de-passe/nouveau',
  GB: 'customer-area/password/new',
  DE: 'customer-area/password/new',
  AT: 'customer-area/password/new',
  CH: 'customer-area/password/new',
};

export const ZIP_CODE_REGEX_BY_COUNTRY: Record<Country, RegExp> = {
  FR: /^\d{5}$/,
  // https://stackoverflow.com/a/51885364/13544489
  // https://en.wikipedia.org/wiki/Postcodes_in_the_United_Kingdom#Formatting
  GB: /^([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/,
  DE: /^\d{5}$/,
  AT: /^\d{4}$/,
  CH: /^\d{4}$/,
};

export const ONE_CURRENCY_CENT = 1;
export const ONE_CURRENCY_UNIT = ONE_CURRENCY_CENT * 100;

export const SORTED_PRO_UNIVERSES: Record<Country, UniverseReference[]> = {
  FR: ['cleaning', 'childcare', 'beauty', 'massage', 'haircut', 'sports_coaching'],
  GB: ['cleaning', 'haircut', 'beauty', 'massage'],
  DE: ['cleaning'],
  AT: ['cleaning'],
  CH: ['cleaning'],
};

export const PRO_PAY_RATE_BY_COUNTRY_UNIVERSE: Record<Country, Record<UniverseReference, number>> = {
  FR: {
    cleaning: ONE_CURRENCY_UNIT * 18,
    childcare: ONE_CURRENCY_UNIT * 18,
    beauty: 0,
    massage: 0,
    haircut: 0,
    sports_coaching: 0,
  },
  GB: {
    cleaning: ONE_CURRENCY_UNIT * 12.5,
    childcare: 0,
    beauty: 0,
    massage: 0,
    haircut: 0,
    sports_coaching: 0,
  },
  DE: {
    cleaning: ONE_CURRENCY_UNIT * 20,
    childcare: 0,
    beauty: 0,
    massage: 0,
    haircut: 0,
    sports_coaching: 0,
  },
  AT: {
    cleaning: ONE_CURRENCY_UNIT * 16,
    childcare: 0,
    beauty: 0,
    massage: 0,
    haircut: 0,
    sports_coaching: 0,
  },
  CH: {
    cleaning: ONE_CURRENCY_UNIT * 26,
    childcare: 0,
    beauty: 0,
    massage: 0,
    haircut: 0,
    sports_coaching: 0,
  },
};

export const PRO_AVERAGE_PAY_RATE_BY_COUNTRY_UNIVERSE: Partial<
  Record<Country, Partial<Record<UniverseReference, number>>>
> = {
  FR: {
    cleaning: ONE_CURRENCY_UNIT * 20,
  },
  GB: {
    cleaning: ONE_CURRENCY_UNIT * 15,
  },
  DE: {
    cleaning: ONE_CURRENCY_UNIT * 20,
  },
  AT: {
    cleaning: ONE_CURRENCY_UNIT * 18,
  },
};

export const PRO_MAX_PAY_RATE_BY_COUNTRY_UNIVERSE: Partial<
  Record<Country, Partial<Record<UniverseReference, number>>>
> = {
  DE: {
    cleaning: ONE_CURRENCY_UNIT * 20,
  },
};

export const PRO_SPONSORSHIP_VALUE_BY_COUNTRY: Record<Country, number> = {
  FR: ONE_CURRENCY_UNIT * 100,
  GB: ONE_CURRENCY_UNIT * 50,
  DE: ONE_CURRENCY_UNIT * 100,
  AT: ONE_CURRENCY_UNIT * 100,
  CH: ONE_CURRENCY_UNIT * 100,
};

export const PRO_APP_DOWNLOAD_URL_BY_COUNTRY: Record<Country, string> = {
  FR: 'https://app-pro.onelink.me/LjAU/appproinstall',
  GB: 'https://app-pro.onelink.me/LjAU/appdownload',
  DE: 'https://app-pro.onelink.me/LjAU/proappinstallde',
  AT: 'https://app-pro.onelink.me/LjAU/proappinstallat',
  CH: 'https://app-pro.onelink.me/LjAU/proappinstallch',
};

// We keep these constants to be more flexible in the future
// cf: https://github.com/wecasa/front/pull/5756#discussion_r2000574336
export const CUSTOMER_AREA_POTENTIAL_PATH_BY_COUNTRY: Record<Country, string> = {
  FR: 'my-customer-area/potential/',
  GB: 'my-customer-area/potential/',
  DE: 'my-customer-area/potential/',
  AT: 'my-customer-area/potential/',
  CH: 'my-customer-area/potential/',
};

export const CUSTOMER_AREA_ORDER_PATH_BY_COUNTRY: Record<Country, string> = {
  FR: 'my-customer-area/order/',
  GB: 'my-customer-area/order/',
  DE: 'my-customer-area/order/',
  AT: 'my-customer-area/order/',
  CH: 'my-customer-area/order/',
};

export const DATE_VALIDATION_MASK_BY_COUNTRY: Record<Country, (RegExp | string)[]> = {
  FR: [/[0-3]/, /\d/, '/', /[0-1]/, /\d/, '/', /[1-2]/, /\d/, /\d/, /\d/],
  GB: [/[0-3]/, /\d/, '/', /[0-1]/, /\d/, '/', /[1-2]/, /\d/, /\d/, /\d/],
  DE: [/[0-3]/, /\d/, '/', /[0-1]/, /\d/, '/', /[1-2]/, /\d/, /\d/, /\d/],
  AT: [/[0-3]/, /\d/, '/', /[0-1]/, /\d/, '/', /[1-2]/, /\d/, /\d/, /\d/],
  CH: [/[0-3]/, /\d/, '/', /[0-1]/, /\d/, '/', /[1-2]/, /\d/, /\d/, /\d/],
};

export const SUPPORTED_PHONE_NUMBER_COUNTRIES = ['gb', 'fr', 'be', 'ch', 'at', 'de'];

type SurchargesConfig = {
  hasEarlyAndLateHourSurcharges: boolean;
  hasSundaySurcharges: boolean;
};

export const SURCHARGES_CONFIG_BY_COUNTRY: Record<Country, SurchargesConfig> = {
  FR: {
    hasEarlyAndLateHourSurcharges: true,
    hasSundaySurcharges: true,
  },
  GB: {
    hasEarlyAndLateHourSurcharges: false,
    hasSundaySurcharges: false,
  },
  DE: {
    hasEarlyAndLateHourSurcharges: false,
    hasSundaySurcharges: false,
  },
  AT: {
    hasEarlyAndLateHourSurcharges: false,
    hasSundaySurcharges: false,
  },
  CH: {
    hasEarlyAndLateHourSurcharges: false,
    hasSundaySurcharges: false,
  },
};

type DaysConfig = {
  hasSundaysDisabled: boolean;
  hasPublicHolidaysDisabled: boolean;
};

export const DISABLED_DAYS_CONFIG_BY_COUNTRY: Record<Country, DaysConfig> = {
  FR: { hasSundaysDisabled: false, hasPublicHolidaysDisabled: false },
  GB: { hasSundaysDisabled: false, hasPublicHolidaysDisabled: false },
  DE: { hasSundaysDisabled: false, hasPublicHolidaysDisabled: false },
  AT: { hasSundaysDisabled: false, hasPublicHolidaysDisabled: false },
  CH: { hasSundaysDisabled: true, hasPublicHolidaysDisabled: true },
};
